import { computed, inject, Injectable, signal, untracked } from '@angular/core';
import {
  FileResponse,
  FileType,
  HttpError,
  UpdateFileTypeRequest,
} from '../../../core/clients/omni-core/models';
import { Api } from '../../../core/clients/omni-core/api';
import { OrganizationsStore } from '../../organizations/store/organizations.store';
import {
  deleteFile,
  getFile,
  listFiles,
  updateFileType,
} from '../../../core/clients/omni-core/functions';

export interface DocumentState {
  data: FileResponse;

  isMarkedForDeletion: boolean;
  deleteLoading: boolean;
  deleteError: HttpError | unknown | null;
}

export interface DocumentsPage {
  page: number;

  documents: DocumentState[];

  isLoading: boolean;
  error: HttpError | unknown | null;
}

export interface DocumentSingle {
  document: FileResponse | null;

  isLoading: boolean;
  error: HttpError | unknown | null;
}

export interface DocumentsFilter {
  data: {
    fileType: FileType | null;
    mimeType: string | null;
    filename: string | null;
    orderBy: 'createdAt' | 'filename' | 'fileType' | 'sizeBytes' | null;
    orderDirection?: 'asc' | 'desc' | null;
  };
}

export const getInitialFilter = (): DocumentsFilter => ({
  data: {
    fileType: null,
    mimeType: null,
    filename: null,
    orderBy: 'createdAt',
    orderDirection: 'desc',
  },
});

export interface DocumentUpdateChanges {
  fileType: string | undefined;
  filename: string | undefined;
}

@Injectable()
export class DocumentsStore {
  private api = inject(Api);
  private organizationsStore = inject(OrganizationsStore);

  #currentPage = signal<number>(1);
  #limit = signal<number>(20);
  #total = signal<number>(0);
  #pages = signal<Record<number, DocumentsPage>>({});

  #singles = signal<Record<string, DocumentSingle>>({});

  isLoading = computed(() => Object.values(this.#pages()).some((page) => page.isLoading));
  pages = computed(() => Object.values(this.#pages()));
  total = computed(this.#total);
  areAllPagesPresent = computed(() => this.#currentPage() * this.#limit() >= this.#total());
  filter = signal<DocumentsFilter>(getInitialFilter());
  isInitialFilterActive = computed(() => {
    const filter = this.filter();
    return !filter.data.fileType && !filter.data.filename && !filter.data.mimeType;
  });
  limit = computed(this.#limit);

  allDocuments = computed(() => {
    const allDocuments: Record<string, DocumentSingle> = {};
    Object.values(this.#pages()).forEach((page) =>
      page.documents.forEach((document) => {
        allDocuments[document.data.id] = { document: document.data, isLoading: false, error: null };
      }),
    );
    return {
      ...allDocuments,
      ...this.#singles(),
    };
  });

  private patchPage(page: number, partial: Partial<DocumentsPage>): void {
    this.#pages.update((pages) => {
      const currentPage: DocumentsPage = pages[page] || {
        page,
        documents: [],
        isLoading: false,
        error: null,
      };
      return {
        ...pages,
        [page]: {
          ...currentPage,
          ...partial,
        },
      };
    });
  }

  async loadPage(page: number): Promise<void> {
    this.#currentPage.set(page);
    const limit = this.#limit();
    this.patchPage(page, {
      isLoading: true,
      error: null,
    });

    try {
      const orgaId = untracked(() => this.organizationsStore.selectedId());
      if (!orgaId) {
        throw new Error('No organization selected');
      }
      const filter = this.filter().data;
      const res = await this.api.invoke(listFiles, {
        limit,
        offset: (page - 1) * limit,
        oid: orgaId,
        filename: filter.filename !== null ? filter.filename : undefined,
        fileType: filter.fileType ? [filter.fileType] : undefined,
        mimeType: filter.mimeType ? filter.mimeType : undefined,
        orderBy: filter.orderBy !== null ? filter.orderBy : undefined,
        orderDirection: filter.orderDirection !== null ? filter.orderDirection : undefined,
      });
      this.#total.set(res.total);
      this.patchPage(page, {
        isLoading: false,
        documents: res.items.map((data) => ({
          data,
          isMarkedForDeletion: false,
          deleteLoading: false,
          deleteError: null,
        })),
      });
    } catch (error) {
      this.patchPage(page, {
        isLoading: false,
        error,
      });
    }
  }

  loadNextPage() {
    if (this.isLoading() || this.areAllPagesPresent()) {
      return;
    }
    const currentPage = this.#currentPage();
    return this.loadPage(currentPage + 1);
  }

  private patchSingle(id: string, document: Partial<DocumentSingle>) {
    this.#singles.update((singles) => ({
      ...singles,
      [id]: {
        ...singles[id],
        ...document,
      },
    }));
  }

  async loadSingle(id: string): Promise<void> {
    this.#singles.update((s) => ({
      ...s,
      [id]: {
        document: null,
        isLoading: true,
        error: null,
      },
    }));
    try {
      const orgaId = untracked(() => this.organizationsStore.selectedId());
      if (!orgaId) {
        throw new Error('No organization selected');
      }
      const res = await this.api.invoke(getFile, {
        oid: orgaId,
        id,
      });
      this.patchSingle(id, {
        document: res,
        isLoading: false,
      });
    } catch (error) {
      this.patchSingle(id, {
        isLoading: false,
        error,
      });
    }
  }

  onChangeFilter() {
    this.#pages.set({});
    this.loadPage(1);
  }

  reset(): void {
    this.#currentPage.set(1);
    this.#limit.set(20);
    this.#total.set(0);
    this.#pages.set({});
    this.filter.set(getInitialFilter());
  }

  async update(id: string, payload: UpdateFileTypeRequest): Promise<DocumentUpdateChanges> {
    const oid = untracked(() => this.organizationsStore.selectedId());
    if (!oid) {
      throw new Error('No organization selected');
    }
    await this.api.invoke(updateFileType, {
      id,
      oid,
      body: payload,
    });

    const page = this.pages().find((p) => p.documents.some((d) => d.data.id === id));
    if (!page) {
      console.warn('Entry not found on any page');
      return payload;
    }

    const changedData: DocumentUpdateChanges = { fileType: undefined, filename: undefined };

    this.patchPage(page.page, {
      ...page,
      documents: page.documents.map((doc) => {
        if (doc.data.id !== id) {
          return doc;
        }
        changedData.fileType =
          doc.data.fileType !== payload.fileType ? payload.fileType : undefined;
        changedData.filename =
          doc.data.filename !== payload.filename ? payload.filename : undefined;
        return { ...doc, data: { ...doc.data, ...payload } };
      }),
    });

    return changedData;
  }

  private patchDocumentOnPage(id: string, data: Partial<DocumentState>) {
    this.#pages.update((pages) => {
      const newPages: Record<number, DocumentsPage> = {};
      Object.keys(pages).forEach((pageNumberAsStr) => {
        const pageNumber = parseInt(pageNumberAsStr, 10);

        const documents = pages[pageNumber].documents.map((document) =>
          id === document.data.id
            ? {
                ...document,
                ...data,
              }
            : document,
        );

        newPages[pageNumber] = {
          ...pages[pageNumber],
          documents,
        };
      });
      return newPages;
    });
  }

  private removeDocumentFromPage(id: string) {
    let removedCount = 0;
    this.#pages.update((pages) => {
      const newPages: Record<number, DocumentsPage> = {};
      Object.keys(pages).forEach((pageNumberAsStr) => {
        const pageNumber = parseInt(pageNumberAsStr, 10);

        const documents = pages[pageNumber].documents.filter((document) => id !== document.data.id);
        removedCount += pages[pageNumber].documents.length - documents.length;

        newPages[pageNumber] = {
          ...pages[pageNumber],
          documents,
        };
      });
      return newPages;
    });

    if (removedCount > 0) {
      this.#total.update((t) => Math.max(0, t - removedCount));
    }
  }

  markForDeletion(id: string) {
    this.patchDocumentOnPage(id, { isMarkedForDeletion: true });
  }

  unmarkForDeletion(id: string) {
    this.patchDocumentOnPage(id, { isMarkedForDeletion: false });
  }

  unmarkAllForDeletion() {
    this.#pages.update((pages) => {
      const updatedPages: Record<number, DocumentsPage> = {};
      for (const pageStr of Object.keys(pages)) {
        const page = parseInt(pageStr, 10);
        updatedPages[page] = {
          ...pages[page],
          documents: pages[page].documents.map((d) => ({
            ...d,
            isMarkedForDeletion: false,
          })),
        };
      }
      return updatedPages;
    });
  }

  async deleteDocument(id: string) {
    const oid = untracked(() => this.organizationsStore.selectedId());
    if (!oid) {
      throw new Error('No organization selected');
    }
    this.patchDocumentOnPage(id, { deleteLoading: true });
    try {
      await this.api.invoke(deleteFile, {
        id,
        oid,
      });
      this.removeDocumentFromPage(id);
    } catch (error) {
      this.patchDocumentOnPage(id, { deleteLoading: false, deleteError: error });
      throw error;
    }
  }
}
